import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiContentCopy,
  mdiTrashCanOutline,
  mdiArrowLeft,
  mdiPencilOutline,
  mdiDotsVertical,
} from "@mdi/js";
import { useApp } from "app/contexts/AppContext";
import { useInitialFetch } from "app/hooks/useInitialFetch";
import CustomToast from "app/components/common/CustomToast";
import {
  fetchContractById,
  fetchContracts,
  saveContract,
  setContractAsDefault,
  duplicateContract,
  deleteContract,
  renameContract,
  clearSaveStatus,
  clearSetDefaultStatus,
  clearDuplicateStatus,
  clearDeleteStatus,
  clearRenameStatus,
} from "../slice";
import LexicalEditor from "./LexicalEditor";
import ViewMode from "./ViewMode";
import RenameContractModal from "../RenameContractModal";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import SignatureRequiredModal from "../SignatureRequiredModal";
import UnsavedChangesModal from "../UnsavedChangesModal";
import ContractBadges from "../ContractBadges";
import { getSetDefaultState } from "../templateHelpers";
import { useFlashToast } from "../useFlashToast";
import { SIGNATURE_VARIABLE_KEY } from "../variables";
import "../contractManagement.css";
import "./contractEditor.css";

// The saved contract HTML stores variables as `{Var_Name}` placeholders
// (matches the backend's persistence format). Check the plain-text form.
const hasSignatureChip = (html) => {
  if (!html) return false;
  return html.includes(`{${SIGNATURE_VARIABLE_KEY}}`);
};

const ContractEditor = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    device: { isMobile },
  } = useApp();

  const {
    templates,
    selectedTemplate,
    getTemplateStatus,
    saveTemplateStatus,
    setDefaultStatus,
    duplicateTemplateStatus,
    duplicatedTemplateId,
    deleteTemplateStatus,
    deletedFallbackName,
    renameTemplateStatus,
  } = useSelector((state) => state.contractManagement);

  const initialMode = searchParams.get("mode") === "edit" ? "edit" : "view";
  const [mode, setMode] = useState(initialMode);
  const [dirty, setDirty] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [unsavedAction, setUnsavedAction] = useState(null);
  const { toast, showToast, hideToast, navigateWithToast } = useFlashToast();

  // Live draft of editor content, read only at save/validate time. A ref (not
  // state) so keystrokes don't re-render the editor subtree.
  const draftHtmlRef = useRef("");

  // The list isn't rendered here, but deleteContract reads it from the store to
  // tell whether the row it removed was the default. Direct URL hits land on
  // this page without going via the list, so make sure it's there.
  useInitialFetch(() => dispatch(fetchContracts()), {
    skip: templates.length > 0,
  });

  // Load the contract this page is for. Independent of the list above: the
  // detail endpoint stands on its own, so this runs in parallel rather than
  // waiting, and no list mutation can retrigger it.
  useEffect(() => {
    if (id == null) return;
    dispatch(fetchContractById(id));
  }, [id, dispatch]);

  // Drop the ?mode=edit query param after we consume it so refresh-in-place
  // doesn't keep reopening edit mode after Save.
  useEffect(() => {
    if (searchParams.get("mode")) {
      const next = new URLSearchParams(searchParams);
      next.delete("mode");
      setSearchParams(next, { replace: true });
    }
    // run once on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed the editor draft from the loaded template
  useEffect(() => {
    if (selectedTemplate) {
      draftHtmlRef.current = selectedTemplate.content_html || "";
      setDirty(false);
    }
  }, [selectedTemplate?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save success → toast, exit edit
  useEffect(() => {
    if (saveTemplateStatus === "success") {
      showToast(
        `"${selectedTemplate?.name || "Template"}" saved successfully.`
      );
      setDirty(false);
      setMode("view");
      dispatch(clearSaveStatus());
    }
  }, [saveTemplateStatus, selectedTemplate?.name, dispatch, showToast]);

  useEffect(() => {
    if (setDefaultStatus === "success") {
      showToast("Default template updated.");
      dispatch(clearSetDefaultStatus());
    }
  }, [setDefaultStatus, dispatch, showToast]);

  useEffect(() => {
    if (renameTemplateStatus === "success") {
      showToast("Template renamed successfully.");
      setShowRename(false);
      dispatch(clearRenameStatus());
    }
  }, [renameTemplateStatus, dispatch, showToast]);

  // Delete returns to the list; the toast rides along so it renders there
  // (bottom-right) rather than on this unmounting page.
  useEffect(() => {
    if (deleteTemplateStatus === "success") {
      const name = selectedTemplate?.name;
      const base = name ? `Template "${name}" deleted.` : "Template deleted.";
      const msg = deletedFallbackName
        ? `${base} "${deletedFallbackName}" is now your default.`
        : base;
      dispatch(clearDeleteStatus());
      navigateWithToast("/contracts", msg);
    }
  }, [
    deleteTemplateStatus,
    deletedFallbackName,
    selectedTemplate,
    dispatch,
    navigateWithToast,
  ]);

  useEffect(() => {
    if (
      duplicateTemplateStatus === "success" &&
      duplicatedTemplateId !== null
    ) {
      const newId = duplicatedTemplateId;
      dispatch(clearDuplicateStatus());
      navigateWithToast(
        `/contracts/${newId}`,
        "Template duplicated successfully."
      );
    }
  }, [
    duplicateTemplateStatus,
    duplicatedTemplateId,
    dispatch,
    navigateWithToast,
  ]);

  // beforeunload guard while dirty
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const enterEdit = () => {
    if (selectedTemplate?.is_system) return;
    draftHtmlRef.current = selectedTemplate?.content_html || "";
    setDirty(false);
    setMode("edit");
  };

  const cancelEdit = () => {
    if (dirty) {
      setUnsavedAction(() => () => {
        setDirty(false);
        setMode("view");
        draftHtmlRef.current = selectedTemplate?.content_html || "";
      });
      return;
    }
    setMode("view");
  };

  const handleSave = () => {
    if (!selectedTemplate) return;
    if (!hasSignatureChip(draftHtmlRef.current)) {
      setShowSignatureModal(true);
      return;
    }
    dispatch(
      saveContract({
        contractId: selectedTemplate.id,
        content_html: draftHtmlRef.current,
      })
    );
  };

  const handleBack = () => {
    if (dirty) {
      setUnsavedAction(() => () => {
        setDirty(false);
        navigate("/contracts");
      });
      return;
    }
    navigate("/contracts");
  };

  const handleSetDefault = () => {
    if (!selectedTemplate) return;
    dispatch(setContractAsDefault(selectedTemplate.id));
  };

  const handleDuplicate = () => {
    if (!selectedTemplate) return;
    dispatch(duplicateContract(selectedTemplate.id));
  };

  const confirmDelete = () => {
    if (!selectedTemplate) return;
    dispatch(deleteContract(selectedTemplate.id));
    setShowDelete(false);
  };

  const submitRename = (name) => {
    if (!selectedTemplate) return;
    dispatch(renameContract({ contractId: selectedTemplate.id, name }));
  };

  const { disabled: setDefaultDisabled, title: setDefaultTitle } =
    getSetDefaultState(selectedTemplate);
  const deleteDisabled = !selectedTemplate || selectedTemplate.is_system;
  const editDisabled = !selectedTemplate || selectedTemplate.is_system;

  const isMobileLayout = isMobile;
  const isSaving = saveTemplateStatus === "loading";

  const titleNode = useMemo(() => {
    return (
      <div className="contract-editor-title-block">
        <div className="contract-editor-title-line">
          <h1 className="contract-editor-title">
            {selectedTemplate?.name || "Contract Template"}
          </h1>
          {mode === "view" && !selectedTemplate?.is_system && (
            <button
              type="button"
              className="contract-editor-rename-btn"
              onClick={() => setShowRename(true)}
              aria-label="Rename template"
              title="Rename template"
            >
              <Icon path={mdiPencilOutline} size={0.67} />
            </button>
          )}
        </div>
        {mode === "view" && <ContractBadges template={selectedTemplate} />}
      </div>
    );
  }, [mode, selectedTemplate]);

  if (
    getTemplateStatus === "loading" ||
    (!selectedTemplate && templates.length === 0)
  ) {
    return (
      <div className="contract-loading">
        <div className="contract-loading-spinner" />
      </div>
    );
  }

  if (!selectedTemplate) {
    return (
      <div className="contract-editor-page">
        <div className="contract-editor-header">
          <button
            type="button"
            className="contract-editor-back"
            onClick={() => navigate("/contracts")}
          >
            <Icon path={mdiArrowLeft} size={0.58} /> Back to Contract Management
          </button>
          <p>Contract template not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contract-editor-page">
      <div className="contract-editor-header">
        <button
          type="button"
          className="contract-editor-back"
          onClick={handleBack}
        >
          <Icon path={mdiArrowLeft} size={0.58} /> Back to Contract Management
        </button>

        <div
          className={`contract-editor-header-row ${
            isMobileLayout ? "mobile" : ""
          }`}
        >
          {titleNode}

          {/* Right-side action group; hide on mobile edit (shown at bottom instead) */}
          {!(mode === "edit" && isMobileLayout) && (
            <div className="contract-editor-actions">
              {mode === "view" ? (
                <>
                  <Dropdown align="end">
                    <Dropdown.Toggle
                      as="button"
                      className="contract-editor-more-btn"
                      aria-label="More actions"
                    >
                      <Icon path={mdiDotsVertical} size={0.67} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="contract-dropdown-menu">
                      <Dropdown.Item onClick={handleDuplicate}>
                        <Icon
                          path={mdiContentCopy}
                          size={0.7}
                          className="me-2"
                        />
                        Duplicate
                      </Dropdown.Item>
                      <Dropdown.Item
                        disabled={deleteDisabled}
                        onClick={() => setShowDelete(true)}
                        className={
                          deleteDisabled ? "" : "contract-dropdown-danger"
                        }
                        title={
                          deleteDisabled
                            ? "System Built-in templates cannot be deleted."
                            : undefined
                        }
                      >
                        <Icon
                          path={mdiTrashCanOutline}
                          size={0.7}
                          className="me-2"
                        />
                        Delete
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <button
                    type="button"
                    className="contract-btn contract-btn-outline"
                    onClick={enterEdit}
                    disabled={editDisabled}
                    title={
                      editDisabled
                        ? "System Built-in templates cannot be edited."
                        : "Edit contract content"
                    }
                  >
                    <Icon
                      path={mdiPencilOutline}
                      size={0.58}
                      className="me-1"
                    />
                    Edit
                  </button>
                  <button
                    type="button"
                    className="contract-btn contract-btn-primary"
                    onClick={handleSetDefault}
                    disabled={setDefaultDisabled}
                    title={setDefaultTitle}
                  >
                    Set as Default
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="contract-btn contract-btn-outline-muted"
                    onClick={cancelEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="contract-btn contract-btn-primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="contract-editor-body">
        {mode === "view" ? (
          <ViewMode contentHtml={selectedTemplate.content_html} />
        ) : (
          <>
            <LexicalEditor
              initialContent={selectedTemplate.content_html || ""}
              onChange={(html) => {
                draftHtmlRef.current = html;
                if (!dirty) setDirty(true);
              }}
              onUploadError={(msg) => showToast(msg, "error")}
            />
            <div className="contract-editor-note">
              Variables will be filled in automatically from job data when the
              contract is sent.
            </div>
            {isMobileLayout && (
              <div className="contract-editor-mobile-actions">
                <button
                  type="button"
                  className="contract-btn contract-btn-outline-muted contract-btn-flex"
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="contract-btn contract-btn-primary contract-btn-flex"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving…" : "Save"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <RenameContractModal
        show={showRename}
        initialName={selectedTemplate.name}
        onClose={() => setShowRename(false)}
        onSubmit={submitRename}
      />
      <DeleteConfirmationModal
        show={showDelete}
        templateName={selectedTemplate.name}
        isDeleting={deleteTemplateStatus === "loading"}
        onClose={() => setShowDelete(false)}
        onConfirm={confirmDelete}
      />
      <SignatureRequiredModal
        show={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
      />
      <UnsavedChangesModal
        show={!!unsavedAction}
        onClose={() => setUnsavedAction(null)}
        onLeave={() => {
          const cb = unsavedAction;
          setUnsavedAction(null);
          if (cb) cb();
        }}
      />

      <CustomToast
        show={toast.show}
        onClose={hideToast}
        message={toast.message}
        variant={toast.variant}
        position="bottom-end"
        isMobile={isMobile}
        autohide
      />
    </div>
  );
};

export default ContractEditor;
